
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import { numberAbbreviation } from '@/helpers/numberAbbreviation';
import { numFormat } from '@/helpers/numFormat';
import { productAlias } from '@/helpers/productAlias';
import { customerAlias } from '@/helpers/customerAlias';
import { SalesVariance } from '@/models/page-models/page-items/SalesVariance';

interface YearQuantities {
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  total: number;
}

interface ApiVarianceItem {
  itemId?: number | string;
  product?: any;
  group?: { _id: number; name: string };
  customer?: { _id: number; name: string; alias: string };
  salesman?: { _id: number; displayName: string };
  // Supports both array and object quantities
  forecastQuantities?: number[]; 
  actualQuantities?: number[];
  forecast?: YearQuantities;
  invoiced?: YearQuantities;
}

interface ProcessedVarianceItem {
  itemId: number | string;
  product?: any;
  customer?: any;
  groupName?: string;
  forecastQuantities: YearQuantities;
  invoicedQuantities: YearQuantities;
}

interface GroupedVarianceData {
  groupName: string;
  forecastQuantities: YearQuantities;
  invoicedQuantities: YearQuantities;
  subItemsVarianceData: ProcessedVarianceItem[];
}

export default Vue.extend({
  name: 'SalesVariance',
  props: {
    input: { type: Object, default: () => {} } as PropOptions<SalesVariance>,
    getVariable: { type: Function, default: () => () => {} },
  },
  data() {
    return {
      quarters: ['q1', 'q2', 'q3', 'q4', 'total'] as Array<keyof YearQuantities>,
    };
  },
  computed: {
    varianceData(): any {
      return this.getVariable(this.input.varianceDataVariable) || { salesVariance: [], year: new Date().getFullYear() };
    },
    isCustomerMode(): boolean {
      return this.input.mode === 'customer';
    },
    groupedVarianceData(): GroupedVarianceData[] {
      const rawList: ApiVarianceItem[] = _.get(this.varianceData, 'salesVariance', []);
      
      const processedList: ProcessedVarianceItem[] = rawList.map(item => {
        const resolvedId = this.isCustomerMode 
          ? _.get(item, 'customer._id') 
          : item.itemId;

        const rawForecast = item.forecast || item.forecastQuantities;
        const rawInvoiced = item.invoiced || item.actualQuantities;

        return {
          ...item,
          itemId: resolvedId || 'N/A',
          forecastQuantities: this.normalizeQuantities(rawForecast),
          invoicedQuantities: this.normalizeQuantities(rawInvoiced),
          groupName: this.isCustomerMode
            ? _.get(item, 'group.name', 'Unassigned')
            : _.get(item, 'product.productLine', '-'),
        };
      });

      const groupedItems = _.groupBy(processedList, 'groupName');

      const groups = _.map(Object.keys(groupedItems), (groupName) => {
        const subItems = _.orderBy(groupedItems[groupName], ['forecastQuantities.total'], ['desc']);
        return {
          groupName,
          forecastQuantities: this.sumQuantities(subItems.map(i => i.forecastQuantities)),
          invoicedQuantities: this.sumQuantities(subItems.map(i => i.invoicedQuantities)),
          subItemsVarianceData: subItems
        };
      });

      return _.orderBy(groups, ['forecastQuantities.total'], ['desc']);
    },
    displayInSAR(): boolean {
      return this.getVariable(this.input.displaySarVariable) || false;
    },
    grandTotals(): { forecast: YearQuantities; invoiced: YearQuantities } {
      const groups = this.groupedVarianceData;
      return {
        forecast: this.sumQuantities(groups.map(g => g.forecastQuantities)),
        invoiced: this.sumQuantities(groups.map(g => g.invoicedQuantities)),
      };
    }
  },
  methods: {
    getQuarterLabel(q: string): string {
      if (q === 'total') return 'Total';
      return q.toUpperCase();
    },
    getItemName(item: ProcessedVarianceItem): string {
      return this.isCustomerMode ? customerAlias(item.customer) : productAlias(item.product);
    },
    getItemUnit(item: ProcessedVarianceItem): string {
      return this.isCustomerMode ? 'SAR' : _.get(item, 'product.packingMode.0.unit1', '-');
    },
    getItemLink(item: ProcessedVarianceItem): string | object {
      if (this.isCustomerMode) {
        return {
          path: '/customer-sales-variance',
          query: { customer: item.itemId, year: this.varianceData.year }
        };
      }
      return `/products/${item.itemId}`;
    },

    normalizeQuantities(data: number[] | YearQuantities | undefined): YearQuantities {
      if (!data) {
        return { q1: 0, q2: 0, q3: 0, q4: 0, total: 0 };
      }

      if (Array.isArray(data)) {
        const q1 = Number(data[0]) || 0;
        const q2 = Number(data[1]) || 0;
        const q3 = Number(data[2]) || 0;
        const q4 = Number(data[3]) || 0;
        return { q1, q2, q3, q4, total: q1 + q2 + q3 + q4 };
      }

      const obj = data as YearQuantities;
      const q1 = Number(obj.q1) || 0;
      const q2 = Number(obj.q2) || 0;
      const q3 = Number(obj.q3) || 0;
      const q4 = Number(obj.q4) || 0;
      const total = Number(obj.total) || (q1 + q2 + q3 + q4);

      return { q1, q2, q3, q4, total };
    },

    sumQuantities(list: YearQuantities[]): YearQuantities {
      const sumKey = (key: keyof YearQuantities) => 
        list.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

      return {
        q1: sumKey('q1'),
        q2: sumKey('q2'),
        q3: sumKey('q3'),
        q4: sumKey('q4'),
        total: sumKey('total'),
      };
    },

    calculateVariance(forecast: number, invoiced: number): string {
      const f = Number(forecast || 0);
      const i = Number(invoiced || 0);
      
      if (f === 0 && i === 0) return '0%';
      if (f === 0) return '100%';
      
      const percent = ((i - f) / f) * 100;
      return `${Math.round(percent)}%`;
    },

    showQuantityOrSAR(val: number | string): string | number {
      const isCurrency = this.displayInSAR || this.isCustomerMode;
      const numVal = Number(val || 0);
      return isCurrency ? numFormat(numVal, true) : numberAbbreviation(numVal);
    }
  }
});
